import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import CheckPermissions from "../../authorization/authorization";

/**
 * Mapping of table routes to their corresponding form paths and required permissions.
 * @constant {Object.<string, {path: string, module: string[]}>}
 */
const permissionMapping = {
    "/new-connection-table": { path: "/new-connection-form", module: ["General Services", "New Connection", "Basic Permissions", "Create"] },
    "/reconnection-table": { path: "/reconnection-form", module: ["General Services", "Reconnection", "Basic Permissions", "Create"] },
    "/bill-correction-table": { path: "/bill-correction-form", module: ["Billing and Collection", "Bill Correction", "Basic Permissions", "Create"] },
    "/change-of-attributes-table": { path: "/change-of-attributes-form/name", module: ["General Services", "Change of Attributes", "Basic Permissions", "Create"] },
    "/change-of-load-table": { path: "/change-of-attributes-form/load", module: ["General Services", "Change of Load", "Basic Permissions", "Create"] },
    "/change-of-tariff-table": { path: "/change-of-attributes-form/tariff", module: ["General Services", "Change of Tariff", "Basic Permissions", "Create"] },
    "/net-metering": { path: "/net-metering/net-metering-form", module: ["General Services", "Shift to Net Meter", "Basic Permissions", "Create"] },
    "/meter-change-request-table": { path: "/meter-change-request-form", module: ["General Services", "Meter Change Order", "Basic Permissions", "Create"] },
    "/meter-reading-correction-table": { path: "/meter-reading-correction-form", module: ["Billing and Collection", "Meter Reading Correction", "Basic Permissions", "Create"] },
    "/due-date-extension-table": { path: "/due-date-extension-form", module: ["Billing and Collection", "Due Date Extension", "Basic Permissions", "Create"] },
    "/adjust-last-bill-table": { path: "/adjust-last-bill-form", module: ["Billing and Collection", "Due Date Extension", "Basic Permissions", "Create"] },
    "/amend-2636-bill-table": { path: "/amend-2636-bill-form", module: ["Billing and Collection", "Due Date Extension", "Basic Permissions", "Create"] },
    "/installments-table": { path: "/installments-form", module: ["Billing and Collection", "Installments", "Basic Permissions", "Create"] },
    "/set-aside-table": { path: "/set-aside-form", module: ["Billing and Collection", "Set Aside", "Basic Permissions", "Create"] },
    "/power-complaint-table": { path: "/power-complaint-form", module: ["Power Complaints"] },
    "/user-management": { path: "/user-management/new-user", module: ["Access Management", "User Management", "CSD Management", "Create"] },
    "/role-management": { path: "/role-management/new-role", module: ["Access Management", "Role Management", "Create"] },
    "/permission-management": { path: "/permission-management/new-permission", module: ["Access Management", "Permission Management", "Create"] },
    "/company-management": { path: "/company-management/new-company", module: ["Organization Management", "Company Management", "Create"] },
    "/circle-management": { path: "/circle-management/new-circle", module: ["Organization Management", "Circle Management", "Create"] },
    "/division-management": { path: "/division-management/new-division", module: ["Organization Management", "Division Management", "Create"] },
    "/support-center-management": { path: "/support-center-management/new-support-center", module: ["Organization Management", "CSC Management", "Create"] },
    "/sub-division-management": { path: "/sub-division-management/new-sub-division", module: ["Organization Management", "Sub Division Management", "Create"] },
};

/**
 * Checks if a user has the necessary permissions.
 * @param {string[]} permissions - The user's permissions.
 * @param {string[]} moduleHierarchy - The required permissions for the module.
 * @returns {boolean} - Returns true if the user has the required permissions, otherwise false.
 */
const hasPermission = (permissions, moduleHierarchy) => {
    if (!permissions || !moduleHierarchy) return false;
    return CheckPermissions(permissions, ...moduleHierarchy);
};

/**
 * A button component that navigates to a form page based on the current route,
 * only if the user has the required permissions.
 *
 * @component
  * @memberof Components
 * @param {Object} props - The properties for the AddButton component.
 * @param {string[]} props.permissions - The list of permissions granted to the user.
 * @param {boolean} props.isButtonVisible - Determines whether the button should be visible.
 * @returns {JSX.Element|null} - The button component if authorized, otherwise null.
 */
const AddButton = ({ permissions, isButtonVisible }) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Get the current route
    const currentPage = location.pathname.replace(/\/$/, "") || "/";
    const permissionDetails = permissionMapping[currentPage];


    /**
     * Determines if the user is authorized to see the button.
     * @type {boolean}
     */
    const isAuthorized = useMemo(() => {
        return permissionDetails ? hasPermission(permissions, permissionDetails.module) : false;
    }, [permissions, permissionDetails]);

    // If button should not be visible, user is not authorized, or no permission details, return null
    if (!isButtonVisible || !isAuthorized || !permissionDetails) return null;

    return (
        <button
            style={{
                backgroundColor: "#B01DB1",
                color: "white",
                width: "40px",
                height: "40px",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
            }}
            onClick={() => navigate(permissionDetails.path)}
        >
            <AddIcon sx={{ marginLeft: "-10px" }} />
        </button>
    );
};

export default AddButton;
